import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';
import { VerificationStatus } from './auth/VerificationStatus';
import { X, Apple, Facebook, Mail, Shield, AlertCircle } from 'lucide-react';

interface AuthModalProps extends React.PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
  enforceAuth?: boolean;
}
export function AuthModal({ isOpen, onClose, enforceAuth = false }: AuthModalProps) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+44');
  const [error, setError] = useState<string | null>(null);
  const { signIn, signUp, signInWithSocial } = useAuth();
  const [showInitialChoice, setShowInitialChoice] = useState(true);

  useEffect(() => {
    const handlePrefillEmail = (event: CustomEvent<{ 
      email: string;
      firstName: string;
      surname: string;
      phone: string;
      countryCode: string;
    }>) => {
      // Clear any existing error
      setError(null);
      
      // Set form fields from event data
      setEmail(event.detail.email);
      setFirstName(event.detail.firstName || '');
      setSurname(event.detail.surname || '');
      setPhone(event.detail.phone || '');
      setCountryCode(event.detail.countryCode || '+44');
      
      // Skip initial choice and go straight to signup
      setShowInitialChoice(false);
      setIsSignUp(true);
    };

    const modalElement = document.getElementById('auth-modal');
    if (modalElement) {
      modalElement.addEventListener('prefill-email', handlePrefillEmail as EventListener);

      return () => {
        modalElement.removeEventListener('prefill-email', handlePrefillEmail as EventListener);
      };
    }
  }, []);

  useEffect(() => {
    // Reset form when modal opens
    if (isOpen) {
      const pendingRequest = localStorage.getItem('pendingQuoteRequest');
      if (pendingRequest) {
        const { personalInfo } = JSON.parse(pendingRequest);
        if (personalInfo) {
          setEmail(personalInfo.email || '');
          setFirstName(personalInfo.firstName || '');
          setSurname(personalInfo.surname || '');
          setPhone(personalInfo.phone || '');
          setCountryCode(personalInfo.countryCode || '+44');
        }
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSocialSignIn = async (provider: 'apple' | 'facebook' | 'google') => {
    setError(null);
    setIsSubmitting(true);
    try {
      await signInWithSocial(provider);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    setVerificationEmail(null);
    
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      setIsSubmitting(false);
      return;
    }

    const name = `${firstName.trim()} ${surname.trim()}`;

    try {
      if (isSignUp) {
        if (!firstName.trim() || !surname.trim() || !phone.trim()) {
          setError('Please fill in all required fields');
          return;
        }

        const metadata = {
          role: 'customer',
          name,
          firstName,
          surname,
          phone: `${countryCode}${phone}`,
        };

        await signUp(email, password, metadata);
        setVerificationEmail(email);
        
        // Don't close modal - let AuthContext handle the redirect
      } else {
        await signIn(email, password);
        onClose();
      }
    } catch (error) {
      console.error('Auth error:', error);
      if (error instanceof Error && error.message.includes('confirmation link')) {
        setVerificationEmail(email);
      } else {
        setError(
          error instanceof Error 
            ? error.message.includes('Invalid login credentials')
              ? 'Invalid email or password'
              : error.message
            : 'Authentication failed'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      id="auth-modal" 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"
      onClick={(e) => {
        // Only allow closing by clicking outside if auth is not enforced
        if (!enforceAuth && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg p-8 max-w-md w-full relative">
        {showInitialChoice ? (
          <div className="space-y-6 text-center">
            {!enforceAuth && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            )}
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-10 w-10 text-blue-600" />
              <span className="ml-2 text-2xl font-bold text-gray-900">CoverBids</span>
            </div>
            <h2 className="text-xl font-medium text-gray-700">Smart Insurance Bidding</h2>
            <p className="text-center text-gray-600">Do you have an account with us already?</p>
            
            <div className="space-y-4">
              <button
                onClick={() => {
                  // When transitioning to sign in, preserve form data
                  const pendingRequest = localStorage.getItem('pendingQuoteRequest');
                  if (pendingRequest) {
                    const { personalInfo } = JSON.parse(pendingRequest);
                    if (personalInfo) {
                      setEmail(personalInfo.email || '');
                    }
                  }
                  setShowInitialChoice(false);
                  setIsSignUp(false);
                }}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Yes, I want to sign in
              </button>
              
              <button
                onClick={() => {
                  // When transitioning to sign up, preserve form data
                  const pendingRequest = localStorage.getItem('pendingQuoteRequest');
                  if (pendingRequest) {
                    const { personalInfo } = JSON.parse(pendingRequest);
                    if (personalInfo) {
                      setEmail(personalInfo.email || '');
                      setFirstName(personalInfo.firstName || '');
                      setSurname(personalInfo.surname || '');
                      setPhone(personalInfo.phone || '');
                      setCountryCode(personalInfo.countryCode || '+44');
                    }
                  }
                  setShowInitialChoice(false);
                  setIsSignUp(true);
                }}
                className="w-full py-3 px-4 bg-white border-2 border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                No, I need to create an account
              </button>
            </div>
          </div>
        ) : (
          <>
            {!enforceAuth && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            )}
            
            <div className="flex items-center justify-center mb-6">
              <Shield className="h-8 w-8 text-blue-600" />
              <h2 className="text-2xl font-bold ml-2 text-gray-900">
              {isSignUp ? 'Create Account' : 'Sign In'}
              </h2>
            </div>

            {verificationEmail && (
              <VerificationStatus
                email={verificationEmail}
                onResendVerification={async () => {
                  setIsResendingVerification(true);
                  try {
                    const { error } = await supabase.auth.resend({
                      type: 'signup',
                      email: verificationEmail
                    });
                    if (error) throw error;
                    setError('Verification email resent successfully');
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to resend verification email');
                  } finally {
                    setIsResendingVerification(false);
                  }
                }}
                isResending={isResendingVerification}
                error={error}
              />
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignUp && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Surname
                    </label>
                    <input
                      type="text"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-24 p-2 border rounded"
                      >
                        <option value="+44">+44</option>
                        <option value="+353">+353</option>
                        <option value="+1">+1</option>
                        <option value="+33">+33</option>
                        <option value="+49">+49</option>
                      </select>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                        className="flex-1 p-2 border rounded"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-2 px-4 rounded bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Processing...' : isSignUp ? 'Sign Up' : 'Sign In'}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => handleSocialSignIn('apple')}
                  className="flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Apple className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialSignIn('facebook')}
                  className="flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Facebook className="h-5 w-5 text-blue-600" />
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialSignIn('google')}
                  className="flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Mail className="h-5 w-5 text-red-600" />
                </button>
              </div>
            </form>

            <div className="mt-4 text-center space-y-2">
              <button
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-sm text-blue-600 hover:text-blue-500 transition-colors"
              >
                {isSignUp
                  ? 'Already have an account? Sign in'
                  : "Don't have an account? Sign up"}
              </button>
              {!isSignUp && (
                <div>
                  <button
                    onClick={() => {
                      onClose();
                      window.location.href = '/register-insurer';
                    }}
                    className="text-sm text-gray-600 hover:text-gray-500 block w-full transition-colors mt-2"
                  >
                    Become an Insurance Provider
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
